import AddIcon from "@mui/icons-material/Add";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ClearIcon from "@mui/icons-material/Clear";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import {
  Table as MuiTable,
  TableBody,
  TableContainer,
  TableRow,
  Box,
  Popover,
  List,
  ListItem,
  ListItemButton,
  useMediaQuery,
  useTheme,
  TextField,
  ClickAwayListener,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Pagination,
  Grid,
} from "@mui/material";
import PropTypes from "prop-types";
import React, { useState, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import TaskModal from "0.Mine/0.Components/CRM/Leads/TaskModal";
import DynamicDataCaptureModal from "0.Mine/0.Components/Reusable/DataCaptureModal";
import { ExtractEnumStates } from "0.Mine/1.Hooks/utils/helpers";
import { LeadState, DefaultLead } from "0.Mine/3.Interfaces/lead";
import borders from "assets/theme/base/borders";
import typography from "assets/theme/base/typography";
import ArgonAvatar from "components/ArgonAvatar";
import ArgonBadge from "components/ArgonBadge";
import ArgonBox from "components/ArgonBox";
import ArgonButton from "components/ArgonButton";
import ArgonTypography from "components/ArgonTypography";

const LEAD_STATUSES = ExtractEnumStates(LeadState);

const sampleFields = [
  {
    key: "name",
    label: "Nome e cognome",
    type: "text",
    required: true,
    placeholder: "John Doe",
  },
  {
    key: "company",
    label: "Azienda",
    type: "text",
    required: false,
    placeholder: "Company Name",
  },
  {
    key: "email",
    label: "Email",
    type: "email",
    required: true,
  },
  {
    key: "state",
    label: "Lead Status",
    type: "select",
    options: LEAD_STATUSES,
    required: true,
    placeholder: "Select Lead Status",
  },
  {
    key: "value",
    label: "Valore del lead",
    type: "number",
    required: true,
    placeholder: 0,
  },
  {
    key: "phone",
    label: "Telefono",
    type: "text",
    required: false,
    placeholder: "+39 123 456 7890",
  },
  {
    key: "notes",
    label: "Note",
    type: "textarea",
    required: false,
    placeholder: "Additional notes about the lead...",
  },
];

function CRMTable({
  columns,
  rows,
  onStatusChange,
  onCreateLead,
  onValueChange,
  onDeleteLead,
  onPageChange,
  onSearch,
  totalCount = 0,
}) {
  const { size, fontWeightBold } = typography;
  const { borderWidth } = borders;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();

  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [newLead, setNewLead] = useState({ ...DefaultLead });
  const [statusMenuState, setStatusMenuState] = useState({
    anchorEl: null,
    leadId: null,
  });
  const [actionMenuState, setActionMenuState] = useState({
    anchorEl: null,
    leadId: null,
  });

  const [searchFields, setSearchFields] = useState({
    name: "",
    company: "",
    email: "",
  });

  const handleSearchFieldChange = (field) => (event) => {
    const newValue = event.target.value;
    const newSearchFields = {
      ...searchFields,
      [field]: newValue,
    };
    setSearchFields(newSearchFields);

    if (onSearch) {
      onSearch(newSearchFields);
    }
  };

  const [valueEditState, setValueEditState] = useState({
    isEditing: false,
    leadId: null,
    value: "",
    cellElement: null,
  });

  const valueInputRef = useRef(null);

  const navigate = useNavigate();

  const isStatusMenuOpen = Boolean(statusMenuState.anchorEl);
  const isActionMenuOpen = Boolean(actionMenuState.anchorEl);

  const [pagination, setPagination] = useState({
    total: totalCount,
    currentPage: 0,
    pageSize: 10,
    lastPage: Math.ceil(totalCount / 10),
  });

  const handlePageChange = useCallback(
    async (event, newPage) => {
      setPagination((prev) => ({
        ...prev,
        currentPage: newPage - 1,
      }));
      if (onPageChange) {
        await onPageChange(newPage, pagination.pageSize, searchFields);
      }
    },
    [pagination.pageSize, searchFields, onPageChange]
  );

  const paginatedRows = rows;

  const [editModalState, setEditModalState] = useState({
    open: false,
    lead: null,
    cardId: null,
  });

  const handleOpenCreateDialog = useCallback(() => {
    setOpenCreateDialog(true);
  }, []);

  const handleCloseCreateDialog = useCallback(() => {
    setOpenCreateDialog(false);
    setNewLead({ ...DefaultLead });
  }, []);

  const handleOpenStatusMenu = useCallback((event, leadId) => {
    event.stopPropagation();

    setStatusMenuState({
      anchorEl: event.currentTarget,
      leadId,
    });
  }, []);

  const handleCloseStatusMenu = useCallback(() => {
    setStatusMenuState({
      anchorEl: null,
      leadId: null,
    });
  }, []);

  const handleStatusChange = useCallback(
    (status) => {
      if (onStatusChange && statusMenuState.leadId) {
        onStatusChange(statusMenuState.leadId, status);
      }
      handleCloseStatusMenu();
    },
    [statusMenuState.leadId, onStatusChange, handleCloseStatusMenu]
  );

  const handleStartValueEdit = useCallback(
    (event, leadId, currentValue) => {
      event.stopPropagation();

      if (valueEditState.isEditing) {
        setValueEditState({
          isEditing: false,
          leadId: null,
          value: "",
          cellElement: null,
        });
      }

      setValueEditState({
        isEditing: true,
        leadId,
        value: currentValue.toString(),
        cellElement: event.currentTarget,
      });
    },
    [valueEditState.isEditing]
  );

  React.useEffect(() => {
    if (valueEditState.isEditing && valueInputRef.current) {
      valueInputRef.current.focus();
    }
  }, [valueEditState.isEditing]);

  const handleValueChange = useCallback((e) => {
    setValueEditState((prev) => ({
      ...prev,
      value: e.target.value,
    }));
  }, []);

  const handleValueEditComplete = useCallback(() => {
    if (valueEditState.isEditing && onValueChange) {
      const numValue = parseFloat(valueEditState.value);
      if (!isNaN(numValue) && valueEditState.leadId) {
        onValueChange(valueEditState.leadId, numValue);
      }
    }

    setValueEditState({
      isEditing: false,
      leadId: null,
      value: "",
      cellElement: null,
    });
  }, [valueEditState, onValueChange]);

  const handleValueKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter") {
        handleValueEditComplete();
      } else if (e.key === "Escape") {
        setValueEditState({
          isEditing: false,
          leadId: null,
          value: "",
          cellElement: null,
        });
      }
    },
    [handleValueEditComplete]
  );

  const handleOpenActionMenu = useCallback((event, leadId) => {
    event.stopPropagation();
    setActionMenuState({
      anchorEl: event.currentTarget,
      leadId,
    });
  }, []);

  const handleCloseActionMenu = useCallback(() => {
    setActionMenuState({
      anchorEl: null,
      leadId: null,
    });
  }, []);

  const handleDeleteLead = useCallback(() => {
    if (onDeleteLead && actionMenuState.leadId) {
      onDeleteLead(actionMenuState.leadId);
    }
    handleCloseActionMenu();
  }, [actionMenuState.leadId, onDeleteLead, handleCloseActionMenu]);

  const handleEditLead = useCallback(
    (lead) => {
      const enrichedLead = {
        ...lead,
        id: lead.id,
        name: lead.name || "",
        company: lead.company || "",
        email: lead.email || "",
        phone: lead.phone || "",
        value: lead.value || 0,
        notes: lead.notes || "",
        state: lead.state || LeadState.New,
        status: {
          id: lead.state || LeadState.New,
          label: lead.state || LeadState.New,
          color: LEAD_STATUSES[lead.state] || "primary",
        },
        probability: lead.probability || 50,
        dueDate: lead.dueDate ? new Date(lead.dueDate) : new Date(),
        lastContact: lead.lastContact ? new Date(lead.lastContact) : new Date(),
        leadSource: lead.source || "",
        vendor_id: lead.vendor_id || "",
        vendor_name: lead.vendor_name || "",
        vendor_pic: lead.vendor_pic || "",
        businessid: lead.businessid || null,
      };

      setEditModalState({
        open: true,
        lead: enrichedLead,
        cardId: lead.id.toString(),
      });
      handleCloseActionMenu();
    },
    [handleCloseActionMenu]
  );

  const handleCloseEditModal = useCallback(() => {
    setEditModalState({
      open: false,
      lead: null,
      cardId: null,
    });
  }, []);

  const tableHeaders = useMemo(() => {
    return columns
      .filter((column) => column.name !== "actions")
      .map(({ name, align, width }, key) => {
        const isFirstColumn = key === 0;
        const isLastColumn = key === columns.length - 2;

        return (
          <ArgonBox
            key={name}
            component="th"
            width={width || "auto"}
            pt={1.5}
            pb={1.25}
            pl={align === "left" ? (isFirstColumn ? 3 : 1) : 3}
            pr={align === "right" ? (isLastColumn ? 3 : 1) : 3}
            textAlign={align}
            fontSize={size.xxs}
            fontWeight={fontWeightBold}
            color="secondary"
            opacity={0.7}
            sx={({ palette: { light } }) => ({
              borderBottom: `${borderWidth[1]} solid ${light.main}`,
            })}
          >
            {t(`tableHeaders.${name}`).toUpperCase()}
          </ArgonBox>
        );
      });
  }, [columns, size, fontWeightBold, borderWidth, t]);

  const tableRows = useMemo(() => {
    return paginatedRows.map((row, rowIndex) => {
      const rowKey = `row-${rowIndex}`;
      const leadId = row.id || rowIndex;

      const cells = columns
        .filter((column) => column.name !== "actions")
        .map(({ name, align }) => {
          if (name === "state") {
            return (
              <ArgonBox
                key={`${rowKey}-${name}`}
                component="td"
                p={1}
                textAlign={align}
                verticalAlign="middle"
                lineHeight={0.65}
                sx={({ palette: { light } }) => ({
                  borderBottom: row.hasBorder ? `${borderWidth[1]} solid ${light.main}` : null,
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                    cursor: "pointer",
                    "&::after": {
                      content: '"✏️"',
                      position: "absolute",
                      marginLeft: "8px",
                      fontSize: "12px",
                    },
                  },
                  position: "relative",
                  transition: "background-color 0.2s",
                })}
                onDoubleClick={
                  !isMobile ? (event) => handleOpenStatusMenu(event, leadId) : undefined
                }
                onClick={isMobile ? (event) => handleOpenStatusMenu(event, leadId) : undefined}
                aria-haspopup="listbox"
                aria-expanded={isStatusMenuOpen && statusMenuState.leadId === leadId}
                role="button"
                tabIndex={0}
              >
                <ArgonBadge
                  variant="gradient"
                  badgeContent={row[name]}
                  color={row[name] ? LEAD_STATUSES[row[name]] : "primary"}
                  size="sm"
                />
              </ArgonBox>
            );
          }

          if (name === "value") {
            const isEditingThisCell = valueEditState.isEditing && valueEditState.leadId === leadId;

            return (
              <ArgonBox
                key={`${rowKey}-${name}`}
                component="td"
                p={1}
                textAlign={align}
                verticalAlign="middle"
                lineHeight={0.65}
                sx={({ palette: { light } }) => ({
                  borderBottom: row.hasBorder ? `${borderWidth[1]} solid ${light.main}` : null,
                  // Interactive styling for value cell
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                    cursor: "pointer",
                    "&::after": {
                      content: '"✏️"',
                      position: "absolute",
                      marginLeft: "8px",
                      fontSize: "12px",
                    },
                  },
                  position: "relative",
                  transition: "background-color 0.2s",
                })}
                onDoubleClick={
                  !isMobile ? (event) => handleStartValueEdit(event, leadId, row[name]) : undefined
                }
                onClick={
                  isMobile ? (event) => handleStartValueEdit(event, leadId, row[name]) : undefined
                }
                role="button"
                tabIndex={0}
              >
                {isEditingThisCell ? (
                  <ClickAwayListener onClickAway={handleValueEditComplete}>
                    <TextField
                      variant="outlined"
                      size="small"
                      type="number"
                      value={valueEditState.value}
                      onChange={handleValueChange}
                      onKeyDown={handleValueKeyDown}
                      inputRef={valueInputRef}
                      autoFocus
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AttachMoneyIcon fontSize="small" />
                          </InputAdornment>
                        ),
                        sx: {
                          height: "32px",
                          "& input": {
                            py: 0.5,
                            px: 0,
                          },
                        },
                      }}
                      sx={{
                        minWidth: "100px",
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: theme.palette.primary.main,
                          },
                          "&:hover fieldset": {
                            borderColor: theme.palette.primary.dark,
                          },
                        },
                      }}
                    />
                  </ClickAwayListener>
                ) : (
                  <ArgonTypography
                    variant="button"
                    fontWeight="regular"
                    color="secondary"
                    sx={{
                      display: "inline-block",
                      width: "max-content",
                      "&:before": {
                        content: '"€"',
                        opacity: 0.7,
                        marginRight: "2px",
                      },
                    }}
                  >
                    {row[name]}
                  </ArgonTypography>
                )}
              </ArgonBox>
            );
          }

          if (Array.isArray(row[name])) {
            return (
              <ArgonBox
                key={`${rowKey}-${name}`}
                component="td"
                p={1}
                sx={({ palette: { light } }) => ({
                  borderBottom: row.hasBorder ? `${borderWidth[1]} solid ${light.main}` : null,
                })}
              >
                <ArgonBox display="flex" alignItems="center" py={0.5} px={1}>
                  <ArgonBox mr={2}>
                    <ArgonAvatar
                      src={row[name][0]}
                      name={row[name][1]}
                      variant="rounded"
                      size="sm"
                    />
                  </ArgonBox>
                  <ArgonTypography
                    variant="button"
                    fontWeight="medium"
                    sx={{ width: "max-content" }}
                  >
                    {row[name][1]}
                  </ArgonTypography>
                </ArgonBox>
              </ArgonBox>
            );
          }

          return (
            <ArgonBox
              key={`${rowKey}-${name}`}
              component="td"
              p={1}
              textAlign={align}
              verticalAlign="middle"
              lineHeight={0.65}
              sx={({ palette: { light } }) => ({
                borderBottom: row.hasBorder ? `${borderWidth[1]} solid ${light.main}` : null,
              })}
            >
              <ArgonTypography
                variant="button"
                fontWeight="regular"
                color="secondary"
                sx={{ display: "inline-block", width: "max-content" }}
              >
                {row[name]}
              </ArgonTypography>
            </ArgonBox>
          );
        });

      cells.push(
        <ArgonBox
          key={`${rowKey}-actions`}
          component="td"
          p={1}
          textAlign="right"
          sx={({ palette: { light } }) => ({
            borderBottom: row.hasBorder ? `${borderWidth[1]} solid ${light.main}` : null,
          })}
        >
          <IconButton
            size="small"
            onClick={(event) => handleOpenActionMenu(event, leadId)}
            aria-label="row actions"
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </ArgonBox>
      );

      return <TableRow key={rowKey}>{cells}</TableRow>;
    });
  }, [
    paginatedRows,
    columns,
    borderWidth,
    handleOpenStatusMenu,
    isMobile,
    isStatusMenuOpen,
    statusMenuState.leadId,
    valueEditState,
    handleStartValueEdit,
    handleValueChange,
    handleValueKeyDown,
    handleValueEditComplete,
    theme.palette.primary,
    handleOpenActionMenu,
  ]);

  const statusMenu = (
    <Popover
      open={isStatusMenuOpen}
      anchorEl={statusMenuState.anchorEl}
      onClose={handleCloseStatusMenu}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "center",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "center",
      }}
      PaperProps={{
        elevation: 3,
        sx: {
          overflow: "visible",
          mt: 0.5,
          width: "auto",
          minWidth: 150,
          "&:before": {
            content: '""',
            display: "block",
            position: "absolute",
            top: 0,
            left: "50%",
            width: 10,
            height: 10,
            bgcolor: "background.paper",
            transform: "translate(-50%, -50%) rotate(45deg)",
            zIndex: 0,
          },
        },
      }}
    >
      <List dense disablePadding sx={{ py: 0.5 }}>
        {Object.values(LEAD_STATUSES).map((status) => (
          <ListItem key={status} disablePadding disableGutters sx={{ px: 1.5, py: 0.5 }}>
            <ListItemButton
              onClick={() => handleStatusChange(status)}
              dense
              sx={{
                borderRadius: 1,
                py: 0.5,
                px: 1,
              }}
            >
              <ArgonBadge
                variant="gradient"
                badgeContent={status}
                color={LEAD_STATUSES[status]}
                size="sm"
                sx={{ width: "100%" }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Popover>
  );

  const actionMenu = (
    <Menu
      anchorEl={actionMenuState.anchorEl}
      open={isActionMenuOpen}
      onClose={handleCloseActionMenu}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      PaperProps={{
        elevation: 3,
        sx: { minWidth: 180 },
      }}
    >
      <MenuItem
        onClick={() => {
          const lead = rows.find((row) => row.id === actionMenuState.leadId);
          handleEditLead(lead);
        }}
      >
        <ListItemIcon>
          <EditIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>{t("leads.editLead")}</ListItemText>
      </MenuItem>
      <MenuItem onClick={handleDeleteLead} sx={{ color: "error.main" }}>
        <ListItemIcon>
          <DeleteIcon fontSize="small" sx={{ color: "error.main" }} />
        </ListItemIcon>
        <ListItemText>{t("leads.deleteLead")}</ListItemText>
      </MenuItem>
    </Menu>
  );

  const createLeadDialog = (
    <DynamicDataCaptureModal
      isOpen={openCreateDialog}
      onClose={handleCloseCreateDialog}
      onSubmit={onCreateLead}
      initialData={DefaultLead}
      fields={sampleFields}
    />
  );

  const searchComponent = (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        width: "100%",
        maxWidth: "60%",
        mr: 2,
      }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            placeholder={t("leads.searchByName")}
            value={searchFields.name}
            onChange={handleSearchFieldChange("name")}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searchFields.name && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      const newSearchFields = { ...searchFields, name: "" };
                      setSearchFields(newSearchFields);
                      if (!newSearchFields.company && !newSearchFields.email) {
                        onSearch(newSearchFields);
                      }
                    }}
                    edge="end"
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            placeholder={t("leads.searchByCompany")}
            value={searchFields.company}
            onChange={handleSearchFieldChange("company")}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searchFields.company && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      const newSearchFields = { ...searchFields, company: "" };
                      setSearchFields(newSearchFields);
                      if (!newSearchFields.name && !newSearchFields.email) {
                        onSearch(newSearchFields);
                      }
                    }}
                    edge="end"
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            placeholder={t("leads.searchByEmail")}
            value={searchFields.email}
            onChange={handleSearchFieldChange("email")}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searchFields.email && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      const newSearchFields = { ...searchFields, email: "" };
                      setSearchFields(newSearchFields);
                      if (!newSearchFields.name && !newSearchFields.company) {
                        onSearch(newSearchFields);
                      }
                    }}
                    edge="end"
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>
    </Paper>
  );

  return (
    <TableContainer>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: 2,
          position: "relative",
          zIndex: 2,
          gap: 2,
        }}
      >
        {searchComponent}
        <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
          <ArgonButton
            variant="gradient"
            color="primary"
            size="small"
            onClick={handleOpenCreateDialog}
            startIcon={<AddIcon />}
          >
            {t("leads.createLead")}
          </ArgonButton>
          <ArgonButton
            variant="gradient"
            color="secondary"
            size="small"
            onClick={() => navigate("/crm/addBulkLeads")}
            startIcon={<AddIcon />}
          >
            {t("leads.addInBulk")}
          </ArgonButton>
        </Box>
      </Box>
      <MuiTable>
        <ArgonBox component="thead">
          <TableRow>
            {tableHeaders}
            <ArgonBox
              component="th"
              width="60px"
              p={1.5}
              textAlign="right"
              fontSize={size.xxs}
              fontWeight={fontWeightBold}
              color="secondary"
              opacity={0.7}
              sx={({ palette: { light } }) => ({
                borderBottom: `${borderWidth[1]} solid ${light.main}`,
              })}
            />
          </TableRow>
        </ArgonBox>
        <TableBody>{tableRows}</TableBody>
      </MuiTable>
      <Box
        display="flex"
        justifyContent="center"
        py={2}
        sx={{
          borderTop: "1px solid",
          borderColor: "divider",
          width: "100%",
        }}
      >
        <Pagination
          count={Math.max(1, Math.ceil(totalCount / pagination.pageSize))}
          page={pagination.currentPage + 1}
          onChange={handlePageChange}
          color="primary"
          showFirstButton
          showLastButton
        />
      </Box>
      <ArgonBox
        mt={2}
        mb={2}
        pl={3}
        pr={3}
        sx={{
          display: "flex",
          alignItems: "center",
          opacity: 0.6,
        }}
      >
        <ArgonTypography
          variant="caption"
          color="text"
          sx={{
            fontStyle: "italic",
            display: "flex",
            alignItems: "center",
            "&:before": {
              content: '"💡"',
              mr: 1,
              fontSize: "14px",
            },
          }}
        >
          {isMobile ? t("leads.tap") : t("leads.doubleClick")} {t("leads.leadStatusBadge")}
        </ArgonTypography>
      </ArgonBox>
      {statusMenu}
      {actionMenu}
      {createLeadDialog}
      <TaskModal
        open={editModalState.open}
        onClose={handleCloseEditModal}
        lead={editModalState.lead}
        initialStatusOptions={Object.entries(LEAD_STATUSES).map(([label, color]) => ({
          id: label,
          label,
          color,
        }))}
        vendors={[]}
        cardId={editModalState.cardId}
      />
    </TableContainer>
  );
}

CRMTable.defaultProps = {
  columns: [],
  rows: [{}],
  onStatusChange: null,
  onCreateLead: null,
  onValueChange: null,
  onDeleteLead: null,
  onPageChange: null,
  onSearch: null,
  totalCount: 0,
};

CRMTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object),
  rows: PropTypes.arrayOf(PropTypes.object),
  onStatusChange: PropTypes.func,
  onCreateLead: PropTypes.func,
  onValueChange: PropTypes.func,
  onDeleteLead: PropTypes.func,
  onPageChange: PropTypes.func,
  onSearch: PropTypes.func,
  totalCount: PropTypes.number,
};

export default CRMTable;
